import { Divider, Stack, Typography } from '@mui/material';
import type {
  CloudSnapshotsPerVolume,
  InventoryResource,
  SnapshotLocation,
} from '@repo/api-gw-sdk';
import classnames from 'classnames';
import { useEffect, useState } from 'react';

import { MultiAccordionsItem } from '@/components/accordion/multiAccordionsItem';
import ButtonWithIcon from '@/components/buttons/buttonWithIcon';
import { Loader } from '@/components/layout/loading';
import { OtherSnapshotDot } from '@/components/snapshots/otherSnapshotDot';
import SnapshotSelectionEmptyState from '@/components/snapshots/snapshotSelection/snapshotSelectionEmptyState';
import { useViewParameters } from '@/contexts/navigation';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import dayjs, { TIME_FORMAT } from '@/utils/dayjs';

import { Panels } from '../../panels';

interface OtherSnapshotsListProps {
  resource: InventoryResource;
  selectedDate: Date;
}

function groupSnapshotsByStartedAt(snapshots: CloudSnapshotsPerVolume[]) {
  const grouped: Record<
    string,
    { providerVolumeId: string; snapshotLocation: SnapshotLocation }[]
  > = {};

  snapshots
    .filter(
      (snap) =>
        !!snap.providerVolumeId &&
        !!snap.snapshotsData &&
        snap.snapshotsData.every((s) => !!s.snapshotLocation)
    )
    .forEach(({ providerVolumeId, snapshotsData }) => {
      snapshotsData!.forEach((snapshot) => {
        if (!snapshot.startedAt) return;

        const normalizedStartedAt = dayjs
          .utc(snapshot.startedAt)
          .startOf('minute')
          .toISOString();

        if (!grouped[normalizedStartedAt]) {
          grouped[normalizedStartedAt] = [];
        }

        grouped[normalizedStartedAt].push({
          providerVolumeId: providerVolumeId!,
          snapshotLocation: snapshot.snapshotLocation!,
        });
      });
    });

  return grouped;
}

export const OtherSnapshotsList = (props: OtherSnapshotsListProps) => {
  const { selectedDate, resource } = props;
  const dal = useDAL();
  const { isAuthorizedResource } = useRoles();
  const { data: providerSnapshots, isLoading } = dal.convert.listCloudSnapshots(
    resource.id,
    selectedDate
  );
  const snapshots = providerSnapshots?.snapshotsPerVolume || [];
  const volumes = snapshots.map((snap) => snap.providerVolumeId);
  const numOfVols = new Set(volumes).size;
  const groupedByTime = groupSnapshotsByStartedAt(snapshots);
  const filteredTimes = Object.keys(groupedByTime).filter(
    (time) => groupedByTime[time].length === numOfVols
  );
  const [selectedTime, setSelectedTime] = useState<string>(filteredTimes[0]);
  const { rightPanel } = useWorkspace();
  const { removeParam } = useViewParameters();

  useEffect(() => {
    setSelectedTime(filteredTimes[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshots]);

  if (isLoading) {
    return (
      <Stack key='loader' marginY='40px'>
        <Loader />
      </Stack>
    );
  }

  if (!filteredTimes.length) {
    return (
      <Stack marginY='40px'>
        <SnapshotSelectionEmptyState
          description={`Couldn't find related snapshots for volumes`}
        />
      </Stack>
    );
  }

  const onClose = () => {
    rightPanel.setIsOpen(false);
    removeParam('id');
  };

  const onBack = () => {
    rightPanel.setComponent({
      panel: Panels.InventoryInstance,
      props: {
        id: resource.id,
        onClose,
        initialTab: 'eon-snapshots',
      },
    });
  };

  return (
    <Stack width='100%'>
      {filteredTimes.map((time) => {
        const timeSnapshots = groupedByTime[time];
        return (
          <div key={time}>
            <MultiAccordionsItem
              isSelected={selectedTime === time}
              onSelect={() => setSelectedTime(time)}
              isMulti={!!filteredTimes.length}
              Summary={
                <Stack
                  gap='80px'
                  direction='row'
                  alignItems='center'
                  className={classnames('flex-1', {
                    'cursor-pointer': filteredTimes.length,
                  })}
                >
                  <Stack
                    width='132px'
                    direction='row'
                    gap='12px'
                    alignItems='center'
                  >
                    <OtherSnapshotDot />
                    <Typography>
                      {`${dayjs.utc(time).format(TIME_FORMAT)} UTC`}
                    </Typography>
                  </Stack>
                  <Typography variant='body1'>
                    {timeSnapshots.length}{' '}
                    {timeSnapshots.length > 1 ? 'volumes' : 'volume'}
                  </Typography>
                </Stack>
              }
              Details={
                <Stack marginLeft={'212px'} gap='24px'>
                  <Stack direction='column' gap='16px'>
                    {timeSnapshots.map((timeSnap) => {
                      const vol = timeSnap.providerVolumeId;
                      const snapId = timeSnap.snapshotLocation.snapshotId;
                      return (
                        <Stack key={vol} direction='row' gap='24px'>
                          <Typography width='170px'>{vol}</Typography>
                          <Divider orientation='vertical' flexItem />
                          <Typography>{snapId}</Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                  <Stack alignItems='end'>
                    <ButtonWithIcon
                      variant='outlined'
                      icon='material-symbols-join-right-rounded'
                      isAuthorized={isAuthorizedResource(
                        'create:jobs',
                        resource
                      )}
                      text={'Convert to Eon'}
                      onClick={() => {
                        rightPanel.setIsOpen(true);
                        rightPanel.setComponent({
                          panel: Panels.ConvertToSingleEonSnapshot,
                          props: {
                            onClose,
                            onBack,
                            resource: resource,
                            snapshotDetails: timeSnapshots,
                            snapshotDate: time,
                          },
                        });
                      }}
                    />
                  </Stack>
                </Stack>
              }
            />
            <Divider />
          </div>
        );
      })}
    </Stack>
  );
};
